import {
    Row,
    Col,
    Card,
    Table,
    Button,
    Avatar,
    Typography,
    Space,
    Input,
    Tag,
    message
} from "antd";
import {useEffect, useMemo, useState} from "react";
import {Redirect} from "react-router-dom";
import moment from "moment";
import {UserDataDto, UserDataDtoGenderEnum} from "acc-rest-client";
import gender_male2_icon from "../assets/images/gender_male2_icon.png";
import gender_female_icon from "../assets/images/gender_female_icon.png";
import gender_unknown_icon from "../assets/images/gender_uknown_icon.png";
import accountRestService from "../services/acc-rest.service";
import roleRestService from "../services/role-rest.service";
import ManageRolesModal from "../components/dialogs/ManageRolesModal";
import {getCustomUnicodeFlagIcon, getRoleLabel, getUserStatusLabel, formatRoleName} from "../utils/labelHelper";
import {getAuthenticatedUser} from "../utils/authHelper";

const {Title} = Typography;

const statusTemplate = (user) => getUserStatusLabel(user?.approvalStatus);

function ManageRoles() {
    const amjUser = getAuthenticatedUser();
    const [allRoles, setAllRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [isUserLoading, setUserLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [openRolesDialog, setOpenRolesDialog] = useState(false);
    const [rolesUser, setRolesUser] = useState(null);

    useEffect(() => {
        const loadAllRoles = async () => {
            try {
                const roles = await roleRestService.getAllRoles();
                setAllRoles(roles || []);
            } catch (error) {
                message.error({content: "Failed to load roles: " + error, duration: 5});
            }
        };
        loadAllRoles();
    }, []);

    // Load every user holding one of the managed roles. There is no endpoint that returns
    // users-with-roles, so we fan out getUsersByRole per role and merge by id — a user's role set
    // is derived from which role-buckets returned them. allSettled keeps authorized buckets
    // rendering even if one role call fails or is forbidden.
    const fetchAllRoleUsers = async (roles) => {
        const roleNames = (roles || []).map((r) => r.name).filter(Boolean);
        if (roleNames.length === 0) {
            setUsers([]);
            return;
        }
        setUserLoading(true);
        try {
            const settled = await Promise.allSettled(
                roleNames.map((name) =>
                    accountRestService.getUsersByRole(name).then((result) => ({name, result}))
                )
            );
            const byId = new Map();
            settled.forEach((outcome) => {
                if (outcome.status !== "fulfilled") {
                    return;
                }
                const {name, result} = outcome.value;
                (result || [])
                    .filter((u) => u.approvalStatus === "APPROVED")
                    .forEach((u) => {
                        const existing = byId.get(u.id);
                        if (existing) {
                            if (!existing.roles.includes(name)) {
                                existing.roles.push(name);
                            }
                        } else {
                            byId.set(u.id, {...u, roles: [name]});
                        }
                    });
            });
            setUsers([...byId.values()]);
        } catch (error) {
            message.error({content: "Failed to load users: " + error, duration: 5});
            setUsers([]);
        } finally {
            setUserLoading(false);
        }
    };

    useEffect(() => {
        fetchAllRoleUsers(allRoles);
    }, [allRoles]);

    const showRolesModal = (user) => {
        setRolesUser(user);
        setOpenRolesDialog(true);
    };

    const hideRolesModal = () => {
        setRolesUser(null);
        setOpenRolesDialog(false);
        fetchAllRoleUsers(allRoles);
    };

    const filteredUsers = useMemo(() => {
        if (!searchText) return users;
        const term = searchText.toLowerCase();
        return users.filter((u) => {
            const haystack = [
                u.firstName,
                u.lastName,
                u.urduName,
                u.email,
                u.aimsId,
                u.jamaat,
                u.country?.defaultLabel
            ].filter(Boolean).join(" ").toLowerCase();
            return haystack.includes(term);
        });
    }, [users, searchText]);

    const roleLabelByName = useMemo(
        () => new Map(allRoles.map((r) => [r.name, getRoleLabel(r)])),
        [allRoles]
    );

    if (!amjUser?.isRolesManager()) {
        return <Redirect to="/error"/>;
    }

    const columns = [
        {
            title: "Member",
            dataIndex: "",
            sorter: (a: UserDataDto, b: UserDataDto) => a.firstName?.localeCompare(b.firstName),
            render: (user) => (user ?
                <Avatar.Group>
                    <div style={{marginRight: "3px"}}>
                        <Avatar
                            src={user.gender === UserDataDtoGenderEnum.Male ? gender_male2_icon : user.gender === UserDataDtoGenderEnum.Female ? gender_female_icon : gender_unknown_icon}
                            shape="square" size="large">{user.gender}</Avatar>
                    </div>
                    <div className="avatar-info">
                        <Title level={5}>{user.firstName + " " + user.lastName}</Title>
                        <p>{user.email}</p>
                    </div>
                </Avatar.Group> : "N/A")
        },
        {
            title: "Urdu Name",
            dataIndex: "",
            sorter: (a: UserDataDto, b: UserDataDto) => a.urduName?.localeCompare(b.urduName),
            render: (user) => (user?.urduName ? <p>{user.urduName}</p> : "N/A")
        },
        {
            title: "Jamaat",
            dataIndex: "",
            render: (user) => (user ? (
                <div className="author-info">
                    <p>{user.aimsId}</p>
                    <p>
                        <span>
                            {user?.jamaat ? user.jamaat : ""}<br/>
                            {user?.country ? user.country.defaultLabel : ""}&nbsp;
                            {getCustomUnicodeFlagIcon(user?.country?.values?.flag)}
                        </span>
                    </p>
                </div>
            ) : "N/A")
        },
        {
            title: "Roles",
            dataIndex: "",
            filters: allRoles.map((r) => ({text: getRoleLabel(r), value: r.name})),
            filterMultiple: true,
            onFilter: (value, record) => record.roles?.includes(value),
            render: (user) => (user?.roles?.length ?
                <Space size={[0, 4]} wrap>
                    {user.roles.map((r) => <Tag key={r}>{roleLabelByName.get(r) || formatRoleName(r)}</Tag>)}
                </Space> : "N/A")
        },
        {
            title: "Created",
            dataIndex: "",
            sorter: (a: UserDataDto, b: UserDataDto) => moment(a.createdTimestamp).unix() - moment(b.createdTimestamp).unix(),
            render: (user) => (user ? <div align={"center"}>
                <p style={{fontStyle: "italic", fontSize: "small"}}>
                    {moment(user.createdTimestamp).format("DD.MM.yyyy - HH:mm:ss")}
                </p>
            </div> : "N/A")
        },
        {
            title: "Action",
            dataIndex: "",
            render: (user) => (user ?
                <Button type="primary" className="tag-primary" onClick={() => showRolesModal(user)}>
                    Manage Roles
                </Button> : "N/A")
        }
    ];

    const totalCount = filteredUsers.length;

    return (
        <>
            <div className="tabled">
                <Row gutter={[24, 0]}>
                    <Col xs="24" xl={24}>
                        <Card
                            bordered={false}
                            className="criclebox tablespace mb-24"
                            title={`Manage Roles (${totalCount})`}
                            extra={
                                <Space>
                                    <Input
                                        placeholder="Search loaded users..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        style={{width: 220}}
                                    />
                                </Space>
                            }
                        >
                            <div className="table-responsive">
                                <Table
                                    columns={columns}
                                    dataSource={filteredUsers}
                                    rowKey={(record) => record.id}
                                    loading={isUserLoading}
                                    pagination={{
                                        pageSize: 20,
                                        showSizeChanger: true,
                                        pageSizeOptions: ["10", "20", "50", "100"],
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                                    }}
                                    className="ant-border-space"
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
            <ManageRolesModal
                open={openRolesDialog}
                user={rolesUser}
                onClose={hideRolesModal}
            />
        </>
    );
}

export default ManageRoles;
